/**
 * Manpower merge — pure decision logic.
 *
 * Lives in shared/ because the review card and the publish path must compute
 * the same numbers from the same inputs. A second copy of this arithmetic on
 * either side would let the preview and the PATCH disagree.
 *
 * Procore only. See docs/superpowers/specs/2026-09-18-manpower-merge-design.md.
 */

export const MANPOWER_MERGE_RESOLUTIONS = ["update", "add", "keep", "both"] as const;
export type ManpowerMergeResolution = (typeof MANPOWER_MERGE_RESOLUTIONS)[number];

/** 4b vs 4c. Decided solely by comparing the two locations. */
export type ManpowerMergeVariant = "same_location" | "different_location";

/** What the walk's phrasing suggested, independent of whether a conflict exists. */
export type ManpowerMergeHint = "recount" | "additional" | "keep" | null;

export interface ManpowerLocationRef {
  remoteId: string | null;
  label: string | null;
}

export interface ManpowerMergeCandidate {
  remoteId: string;
  workers: number | null;
  hours: number | null;
  locationRemoteId: string | null;
  locationLabel: string | null;
  companyRemoteId: string | null;
  companyLabel: string | null;
  /** Display source line, e.g. "TONY R". */
  createdByLabel: string | null;
  /**
   * The remote record's creation time. Formatted by the client in the
   * device's local timezone — the phone is physically on the job site, so
   * that's the timezone a reviewer expects, and this backend runs in UTC on
   * Render.
   */
  occurredAt: Date | null;
  /** Drives the tile's mark: a LedAI-authored row from an earlier walk vs a PM's. */
  origin: "ledai" | "procore";
  remoteUpdatedAt: Date | null;
}

export interface ManpowerMerge {
  projectId: number;
  /** YYYY-MM-DD, from getVisitLogDate. */
  logDate: string;
  candidates: ManpowerMergeCandidate[];
  targetRemoteId: string;
  variant: ManpowerMergeVariant;
  suggested: ManpowerMergeResolution;
  resolution?: ManpowerMergeResolution;
  baseline: {
    workers: number | null;
    hours: number | null;
    remoteUpdatedAt: Date | null;
  };
  detectedAt: Date;
  /**
   * The company this merge was MATCHED on, as the item carried it at
   * detection time.
   *
   * The reviewer can re-pick the company on the same card, and the new vendor
   * and the resolution are then written in one update: the card shows the new
   * company while `targetRemoteId` still addresses the old one's row, so
   * company A's PM record receives company B's headcount. The push compares
   * these two against the item's CURRENT company and refuses on a mismatch
   * rather than re-detecting against a row nobody reviewed.
   *
   * Optional: a merge stored before this field existed carries neither, and
   * the push then has nothing to compare and proceeds as before.
   */
  matchedCompanyRemoteId?: string;
  matchedCompanyLabel?: string;
  /**
   * The walk's own worker count as it stood BEFORE the reviewer confirmed.
   *
   * Confirming `add` resolves the sum into the record itself — the item's
   * `workers` becomes existing + walk and the decision is persisted as a
   * replace, so what the Details card shows is what actually gets written.
   * That overwrites the walk's own count, so it is kept here: reopening the
   * decision restores it, otherwise a second `add` would sum against the
   * already-summed value (6 + 16 rather than 6 + 10).
   */
  walkWorkersBeforeDecision?: number | null;
  decidedAt?: Date;
  decidedBy?: string;
  /**
   * Set once the resolution has actually been written to Procore. This is the
   * idempotency record for the merge push, and the ONLY thing standing
   * between a routine re-publish and a second application of the resolution.
   *
   * A re-publish is not exceptional: `getApprovedProcoreDailyLogItemsByVisit`
   * returns every approved item regardless of sync status, several call sites
   * publish a visit, and the publish pass reschedules itself. Without this
   * stamp, `add` compounded on every pass (5 -> 9 -> 14 -> 19, each a real
   * write to somebody's live Procore record) and `update` re-read its own
   * write and reported the PM's count as "changed while you were reviewing".
   *
   * Written together with the `synced` stamp on the item, never on a failure,
   * so a failed push is still retryable.
   */
  pushedAt?: Date;
  /**
   * The resolution that `pushedAt` actually applied.
   *
   * The idempotency short-circuit requires this to still equal `resolution`.
   * Without it, a reviewer who reopened a pushed card and switched
   * `update` -> `add` got no PATCH, a `synced` status and an unchanged remote:
   * their new decision was accepted by the UI, persisted, and discarded with
   * only an info log. A mismatch is refused outright rather than auto-applied,
   * because `update` -> `add` after a push double-counts the walk.
   */
  pushedResolution?: ManpowerMergeResolution;
  /**
   * Set immediately BEFORE the PATCH goes out, and only cleared when Procore
   * answers (success stamps `pushedAt`; an HTTP status means the write
   * definitively did not land).
   *
   * `attemptedPushAt` set with no `pushedAt` therefore means "we issued a
   * write and never learned its fate" — a lost response. `add` must refuse in
   * that state: Procore may already hold the incremented count, and re-summing
   * against it went 4 -> 9 -> 14, each a real write to a customer's record.
   */
  attemptedPushAt?: Date;
  /**
   * The worker count the push wrote. Diagnostics — never re-used as a basis.
   *
   * `undefined`, not `null`: the sub-schema drops a null leaf to absent, so a
   * `number | null` type promised a round-trip the storage layer never made.
   */
  pushedWorkers?: number;
  /**
   * The project and record the push landed in. The short-circuit requires
   * BOTH to match the publish currently running, so a site whose Procore link
   * moved re-evaluates from scratch instead of trusting a foreign stamp.
   */
  pushedProjectId?: number;
  pushedRemoteId?: string;
}

/**
 * The ONE coercion for a manpower count, shared by every side of the feature.
 *
 * Daily-log field values are stored unnormalized: the same `workers` leaf
 * arrives as `5` or as `"5"` depending on which path wrote it. The publish
 * path always coerced (`readNumber` in manpowerMergeDetection re-exports
 * this); the review card and the detection inputs used `typeof === "number"`
 * and dropped the string, so the card previewed "Update to — workers" while
 * the PATCH sent 5. Both sides now read counts through here.
 */
export function readManpowerCount(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim()) {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return null;
}

export function isManpowerMergeResolution(value: unknown): value is ManpowerMergeResolution {
  return typeof value === "string" &&
    (MANPOWER_MERGE_RESOLUTIONS as readonly string[]).includes(value);
}

/**
 * Procore location labels arrive with assorted separators and casing
 * ("Tower A › Level 02" vs "Tower A > Level 02"). Normalize before comparing,
 * so a label-only match doesn't fail on punctuation.
 */
export function normalizeLocationLabel(value: string | null | undefined): string | null {
  if (typeof value !== "string") return null;
  const normalized = value
    .replace(/[›»]/g, ">")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();
  return normalized.length > 0 ? normalized : null;
}

function hasLocation(ref: ManpowerLocationRef): boolean {
  return !!ref.remoteId || normalizeLocationLabel(ref.label) !== null;
}

/**
 * "unknown" means at least one side has no location at all. That is NOT a
 * mismatch — per the routing rule it stays on 4b.
 */
export function compareManpowerLocations(
  existing: ManpowerLocationRef,
  walk: ManpowerLocationRef,
): "same" | "different" | "unknown" {
  if (!hasLocation(existing) || !hasLocation(walk)) return "unknown";

  if (existing.remoteId && walk.remoteId) {
    return existing.remoteId === walk.remoteId ? "same" : "different";
  }

  const a = normalizeLocationLabel(existing.label);
  const b = normalizeLocationLabel(walk.label);
  if (a === null || b === null) return "unknown";
  return a === b ? "same" : "different";
}

export function manpowerMergeVariant(
  existing: ManpowerLocationRef,
  walk: ManpowerLocationRef,
): ManpowerMergeVariant {
  return compareManpowerLocations(existing, walk) === "different"
    ? "different_location"
    : "same_location";
}

/**
 * The resulting worker count for a resolution.
 *
 * `both` returns the walk's own count because it creates a separate row — the
 * existing row is untouched, so its count never enters the arithmetic.
 */
export function resolveManpowerWorkers(
  resolution: ManpowerMergeResolution,
  existingWorkers: number | null,
  walkWorkers: number | null,
): number | null {
  switch (resolution) {
    case "keep":
      return existingWorkers;
    case "add":
      if (existingWorkers === null) return walkWorkers;
      if (walkWorkers === null) return existingWorkers;
      return existingWorkers + walkWorkers;
    case "update":
    case "both":
      return walkWorkers === null ? existingWorkers : walkWorkers;
  }
}

/**
 * Display only. Procore computes man_hours itself — never send this.
 */
export function manpowerMergeTotalHours(
  workers: number | null,
  hours: number | null,
): number | null {
  if (workers === null || hours === null) return null;
  return workers * hours;
}

/**
 * Best match first: a same-location row, then the most recently updated.
 * Stable for ties so the card doesn't reshuffle between renders.
 */
export function rankManpowerCandidates(
  candidates: ManpowerMergeCandidate[],
  walkLocation: ManpowerLocationRef,
): ManpowerMergeCandidate[] {
  return candidates
    .map((candidate, index) => ({ candidate, index }))
    .sort((a, b) => {
      const aSame =
        compareManpowerLocations(
          { remoteId: a.candidate.locationRemoteId, label: a.candidate.locationLabel },
          walkLocation,
        ) === "same";
      const bSame =
        compareManpowerLocations(
          { remoteId: b.candidate.locationRemoteId, label: b.candidate.locationLabel },
          walkLocation,
        ) === "same";
      if (aSame !== bSame) return aSame ? -1 : 1;

      const aAt = a.candidate.remoteUpdatedAt?.getTime() ?? null;
      const bAt = b.candidate.remoteUpdatedAt?.getTime() ?? null;
      if (aAt !== bAt) {
        if (aAt === null) return 1;
        if (bAt === null) return -1;
        return bAt - aAt;
      }

      return a.index - b.index;
    })
    .map((entry) => entry.candidate);
}

/**
 * `keep` is never suggested — the design's rules table makes it explicitly
 * reviewer-chosen, for when the walk's count is less trustworthy than what is
 * already logged.
 */
export function suggestManpowerResolution(
  variant: ManpowerMergeVariant,
  hint: ManpowerMergeHint,
): ManpowerMergeResolution {
  if (variant === "different_location") return "both";
  return hint === "additional" ? "add" : "update";
}
